import classNames                  from 'classnames';
import * as PropTypes              from 'prop-types';
import * as React                  from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation }          from 'react-i18next';

import useIsMounted            from '../../hooks/useIsMounted';
import { OffersSlider, Price } from '../../new-components';
import { useCatalog }          from '../../providers/catalog';
import { useUser }             from '../../providers/user';
import checkAccessOffer        from '../../templates/_common/utils/checkAccessOffer';

import CompareButton  from '../buttons/CompareButton';
import RenderButton   from '../buttons/base/RenderButton';
import { ShareBlock } from '../share';

const CatalogItem = function(props) {
  const history  = useHistory();
  const location = useLocation();
  const { t }    = useTranslation();

  const {
          currentOfferId,
          hidePrices,
          isInterior,
          isButtonsNextToSubtitle,
          offers,
          showOffers,
          showTags,
          titleH1,
          shareButtonVisible,
          orientation = 'horizontal',
        } = props;

  const catalog   = useCatalog();
  const user      = useUser();
  const isMounted = useIsMounted();

  const [currentId, setCurrentId] = React.useState(currentOfferId || Object.values(offers)[0].common.offerId);

  const current      = React.useMemo(() => {
    return offers[currentId] || offers[`_${currentId}`];
  }, [currentId, offers]);
  const currentCity  = React.useMemo(() => {
    return user.cities?.find((city) => {
      return city.cityId === user.location.cityId;
    });
  }, [user.cities, user.location.cityId]);
  const inBookmarks  = React.useMemo(() => {
    if (!current) {
      return false;
    }

    return (
      !!user.bookmarks[current.common.modelId]?.[currentId] ||
      !!user.bookmarks[current.common.modelId]?.[`_${currentId}`]
    );
  }, [user.bookmarks, current, currentId]);
  const isAvailable  = React.useMemo(() => {
    if (!current) {
      return false;
    }

    return checkAccessOffer(current, currentCity);
  }, [current, currentCity]);
  const offer        = React.useMemo(() => {
    if (!current) {
      return null;
    }

    return current.common;
  }, [current]);
  const subTitle     = React.useMemo(() => {
    if (!offer) {
      return null;
    }

    if (offer.tags?.find(({ tagId }) => tagId === 2)) {
      return t('client_with_smart_lock');
    }

    if (isInterior || offer.isInterior) {
      return t('client_interior_door');
    }

    if (offer.installationTypeUrlKey === 'v-dom') {
      return t('client_entrance_door_for_house');
    }

    if (offer.installationTypeUrlKey === 'v-kvartiru') {
      return t('client_entrance_door_for_flat');
    }
  }, [isInterior, offer]);
  const toolsButtons = React.useMemo(() => {
    if (!offer) {
      return [];
    }

    const buttons = [
      <RenderButton
        action={(event) => {
          event.stopPropagation();

          user.addToBookmarks(offer);
        }}
        icon={
          <span
            className={
              inBookmarks
                ? 'ic-like-fill'
                : 'ic-like'
            }
          />
        }
        key={'bookmark'}
      />,
    ];

    // Added only after mount, never during hydration: the server HTML has no share button here,
    // and a first client render that adds one breaks hydration of the whole catalog.
    if (isMounted && shareButtonVisible) {
      buttons.push(
        <ShareBlock
          key={'share'}
          link={offer.urlKey}
        />,
      );
    }

    buttons.push(
      <CompareButton
        isCatalogCompareBtn
        isInterior={isInterior}
        key={'compare'}
        offer={offer}
      />,
    );

    return buttons;
  }, [inBookmarks, isInterior, isMounted, offer, shareButtonVisible, currentId]);

  const changeOffer = React.useCallback((offerId) => {
    setCurrentId(offerId);
  }, []);
  const checkOffer  = React.useCallback((offerId) => {
    if (!offerId) {
      return false;
    }

    const ids = Object.keys(offers);

    return ids.includes(offerId.toString()) || ids.includes(`_${offerId}`);
  }, [offers]);

  React.useEffect(() => {
    if (!checkOffer(currentId)) {
      const offer = Object.values(offers)[0].common.offerId;

      setCurrentId(offer);
    }
  }, [currentId, offers]);

  if (!currentId || !checkOffer(currentId) || !offer) {
    return null;
  }

  return (
    <div
      onClick={() => {
        const [, , second] = location.pathname.split('/');

        history.push({
          pathname: offer.urlKey,
          state:    {
            prevUrl: second,
            search:  location.search,
            title:   titleH1,
          },
        });
      }}
      className={classNames('item card', {
        'double':     offer.constructionTypeId === 2 || isInterior || current.common.isInterior || offer.singleSideView,
      })}
    >
      <OffersSlider
        current={currentId}
        isDouble={offer.constructionTypeId === 2 || offer.singleSideView}
        offers={offers}
        onChange={changeOffer}
        orientation={orientation}
        showOffers={showOffers}
        showTags={showTags}
      />
      <div className={'title'}>{offer.label}</div>
      <div className={'bottom_wrapper'}>
        <div className={'sub-title_wrapper'}>
          {subTitle && <div className={'sub-title'}>{subTitle}</div>}
          {offer && !isButtonsNextToSubtitle && (
            <div className={classNames('bottom_btns_container', {
              btns_container_visible: !catalog.filter.open
            })}>{toolsButtons}</div>
          )}
        </div>
        <div
          className={'bottom justifyContent'}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <div className={'bottom_btns_container'}>
            {shareButtonVisible &&
              <ShareBlock link={offer.urlKey} />
            }
            <RenderButton
              icon={<span className={inBookmarks ? 'ic-like-fill' : 'ic-like'} />}
              action={() => {
                user.addToBookmarks(offer);
              }}
            />
          </div>
        </div>
        <div className={'vendor_code_container'}>
          {(
            <>
              {isAvailable ? (
                <>
                  {!hidePrices && !isInterior && !current.common.isInterior && (
                    <Price
                      discountPrice={offer.discountPrice}
                      isMarker={offer.isMarker}
                      isSpecMarker={offer.isSpecMarker}
                      isExpressMarker={offer.isExpressMarker}
                      price={offer.price}
                      showDiscountPrice={offer.showDiscountPrice}
                    />
                  )}
                </>
              ) : (
                <div className={'price'}>
                  {t('client_modelNotAvailable')}
                </div>
              )}
            </>
          )}
          {isButtonsNextToSubtitle && (
            <div className={classNames('bottom_btns_container', { btns_container_visible: !catalog.filter.open })}>
              {toolsButtons}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CatalogItem.propTypes = {
  offers: PropTypes.object.isRequired,
};

export default CatalogItem;